import type * as hast from 'hast';
import type * as mdast from 'mdast';
import rehypeMinifyWhitespace from 'rehype-minify-whitespace';
import rehypeRaw from 'rehype-raw';
import remarkHeadingId from 'remark-heading-id';
import remarkMath from 'remark-math';
import remarkParse from 'remark-parse';
import remarkRehype from 'remark-rehype';
import { unified } from 'unified';
import type { z } from 'zod';

import type { Document } from '@gitbook/slate';
import { createHTTPError, formatErrorForLog, Logger } from '@gitbook/util-debug';

import { footnoteIdToHtmlId, remarkGfm } from '../../utils'; // mdast-util-gfm-table
import { createParsingContext } from '../context';
import { hastToGast } from '../html';
import { defaultParsingOptions, type ParsingOptions } from '../options';
import { remarkEmojis } from './emojis';
import { type FrontmatterRawAttributes, parseFrontmatterOrThrow } from './frontmatter';
import {
    getIncrementalCacheState,
    type MarkdownIncrementalCacheState,
    type MarkdownIncrementalFrontmatterResult,
} from './incremental';
import { parseLiquidProperties, processLiquidTags } from './liquid';

const logger = Logger('parse:markdown');

export type ParseMarkdownIncrementalStats = {
    /** Number of blocks skipped due to incremental cache. */
    blocksSkipped: number;
};

export type ParsedFrontmatterAttributes<
    AttributesSchema extends z.ZodType<any> | undefined = undefined,
> = AttributesSchema extends undefined ? FrontmatterRawAttributes : z.infer<AttributesSchema>;

export type ParseMarkdownResult<AttributesSchema extends z.ZodType<any> | undefined = undefined> = {
    /** Slate document parsed from the markdown input. */
    document: Document;
    /** Frontmatter attributes parsed from the markdown input. */
    attributes: ParsedFrontmatterAttributes<AttributesSchema>;
    /** Incremental cache state to be used for future incremental parsing. */
    incrementalCacheState: MarkdownIncrementalCacheState | null;
    /** Stats about the parsing process. */
    stats: ParseMarkdownIncrementalStats;
    /** Whether a frontmatter was found. */
    hasFrontmatter: boolean;
};

export type ParseMarkdownOptions<AttributesSchema extends z.ZodType<any> | undefined = undefined> =
    Partial<ParsingOptions> & {
        /**
         * Defines the way soft-breaks are handled.
         * By default, soft-breaks are minified and replaced with a whitespace. This matches the behavior of GitHub or other markdown renderers.
         * When parsing Markdown as a loose input text (ex: when pasting from the clipboard), we want to preserve the soft-breaks.
         * @default false.
         */
        preserveSoftBreaks?: boolean;

        /**
         * Parse and extract the frontmatter from the markdown input.
         * @default true.
         */
        attributesSchema?: AttributesSchema;

        /**
         * Maximum offset to use for incremental parsing.
         * It can be used when processing multiple times a similar input where only a head is known.
         * @default Infinity
         */
        maxIncrementalOffset?: number;

        /**
         * Maximum size of the input, in UTF-8 bytes. Past it the parse is refused rather than
         * attempted.
         *
         * Parsing allocates roughly an order of magnitude more than the document it produces, so a
         * large enough input cannot finish inside a worker's 128MB isolate: it is killed mid-parse,
         * which takes down whatever was driving it instead of failing the call. Measured in bytes
         * rather than characters because that is what the cost tracks — the same length of CJK or
         * emoji is two to three times the heap of prose — and it is the measure
         * {@link parseMarkdownPage} already uses.
         *
         * Unset by default: a caller opts in with the ceiling its runtime can afford.
         */
        maxBytes?: number;
    };

/**
 * Parse a markdown string into a GitBook document.
 */
export async function parseMarkdown<
    AttributesSchema extends z.ZodType<any> | undefined = undefined,
>(
    input: string,
    options: ParseMarkdownOptions<AttributesSchema> = defaultParsingOptions,
    incrementalCacheState: MarkdownIncrementalCacheState | null = null
): Promise<ParseMarkdownResult<AttributesSchema>> {
    const {
        preserveSoftBreaks = false,
        maxIncrementalOffset = Infinity,
        maxBytes,
        attributesSchema,
    } = options;

    if (maxBytes !== undefined) {
        const inputBytes = Buffer.byteLength(input, 'utf8');
        if (inputBytes > maxBytes) {
            throw new createHTTPError.BadRequest(
                `This content is too large to process in one piece (${formatKB(inputBytes)}; the maximum is ${formatKB(maxBytes)}). Split it into smaller parts.`
            );
        }
    }

    const parsingOptions: ParsingOptions = {
        ...defaultParsingOptions,
        ...options,
    };
    const context = createParsingContext(parsingOptions);

    try {
        const stats: ParseMarkdownIncrementalStats = {
            blocksSkipped: 0,
        };

        let frontmatter: MarkdownIncrementalFrontmatterResult<
            ParsedFrontmatterAttributes<AttributesSchema>
        >;
        if (incrementalCacheState?.frontmatter) {
            // Re-use the previously parsed frontmatter
            // if it exists
            frontmatter = incrementalCacheState.frontmatter;
        } else {
            // Parse the frontmatter from the input
            const frontmatterParsed =
                parseFrontmatterOrThrow<ParsedFrontmatterAttributes<AttributesSchema>>(input);
            const bodyBegin =
                frontmatterParsed.bodyBegin > 1
                    ? getLineOffset(input, frontmatterParsed.bodyBegin - 1)
                    : 0;
            if (bodyBegin === -1) {
                throw new Error(
                    `Invalid frontmatter: bodyBegin (${frontmatterParsed.bodyBegin}) is out of bounds`
                );
            }
            frontmatter = {
                attributes: frontmatterParsed.attributes,
                bodyBegin,
            };

            if (attributesSchema) {
                const decodedResult = attributesSchema.safeDecode(frontmatter.attributes);
                if (decodedResult.error) {
                    throw new createHTTPError.BadRequest(
                        `Invalid frontmatter: ${decodedResult.error.message}`
                    );
                }
                frontmatter.attributes = decodedResult.data;
            }
        }

        // Process the liquid tags into HTML
        const markdownSource = processLiquidTags(input.slice(frontmatter.bodyBegin));

        const partialMarkdownSource = incrementalCacheState
            ? markdownSource.slice(incrementalCacheState.endIndex)
            : markdownSource;

        const file = await unified()
            // Parse the markdown with GitHub syntax
            .use(remarkParse)
            .use(remarkGfm)
            .use(remarkMath, {
                singleDollarTextMath: false,
            })
            .use(remarkEmojis)
            .use(remarkHeadingId)

            // Transform the markdown into HTML
            .use(remarkRehype, {
                allowDangerousHtml: true,
                handlers: {
                    // mdast-util-to-hast encode the url. we want the original url parsed from markdown.
                    // https://github.com/syntax-tree/mdast-util-to-hast/blob/9.1.2/lib/handlers/link.js#L9
                    link: (state, node: mdast.Link) => {
                        const props: { href: string; title?: string } = { href: node.url };

                        if (node.title !== null && node.title !== undefined) {
                            props.title = node.title;
                        }

                        const element: hast.Element = {
                            type: 'element',
                            tagName: 'a',
                            properties: props,
                            children: state.all(node),
                        };

                        return element;
                    },

                    // rehype-raw removes the `data` generated by `remark-rehype` to store the `meta` of the node.
                    // we need to keep it to be able to retrieve the `meta` later in HTML parsing
                    code: (_state, node: mdast.Code) => {
                        const value = node.value ? `${node.value}\n` : '';
                        const props: {
                            className?: string[];
                            dataMeta?: string;
                        } = {};

                        if (node.lang) {
                            props.className = [`language-${node.lang}`];
                        }

                        if (node.meta) {
                            // If meta is a liquid string that we generated
                            // we replace it by actual data properties on the HTML element
                            const metaProps = parseLiquidProperties(node.meta);

                            // Because HTML data attributes are case insensitive, we store the keys and values
                            // in a separate object when they might be case sensitive or include dashes.
                            // We stringify them in the HTML to prevent losing the information.
                            if (Object.keys(metaProps).length > 0) {
                                props['data-gitbook-props'] = JSON.stringify(metaProps);
                            } else {
                                props.dataMeta = node.meta;
                            }
                        }

                        const code: hast.Element = {
                            type: 'element',
                            tagName: 'code',
                            properties: props,
                            children: [
                                {
                                    type: 'text',
                                    value,
                                },
                            ],
                        };

                        return {
                            type: 'element',
                            tagName: 'pre',
                            properties: {},
                            children: [code],
                        };
                    },

                    // Custom handlers for footnotes as the default one is appending a link back to the reference.
                    // This implementation is more direct, and only creates a single link.
                    footnoteReference: (state, node: mdast.FootnoteReference) => {
                        return {
                            type: 'element',
                            tagName: 'a',
                            properties: {
                                href: `#${footnoteIdToHtmlId(node.identifier)}`,
                                'data-footnote-ref': true,
                            },
                            children: state.all(node),
                        };
                    },
                    footnoteDefinition: (state, node: mdast.FootnoteDefinition) => {
                        return {
                            type: 'element',
                            tagName: 'section',
                            properties: {
                                'data-footnotes': true,
                            },
                            children: [
                                {
                                    type: 'element',
                                    tagName: 'ol',
                                    properties: {},
                                    children: [
                                        {
                                            type: 'element',
                                            tagName: 'li',
                                            properties: {
                                                id: footnoteIdToHtmlId(node.identifier),
                                            },
                                            children: state.all(node),
                                        },
                                    ],
                                },
                            ],
                        };
                    },
                },
            })
            .use(rehypeRaw)

            .use(rehypeMinifyWhitespace, { newlines: preserveSoftBreaks })

            // Process the raw HTML in the markdown
            // Transform the HTML tree into a document
            .use(hastToGast, context)
            .process(partialMarkdownSource);

        const { document: partialDocument, attributes } = (await file.result) as {
            document: Document;
            attributes: FrontmatterRawAttributes;
        };

        const document = partialDocument.merge({
            nodes: incrementalCacheState
                ? incrementalCacheState.blocks.concat(partialDocument.nodes)
                : partialDocument.nodes,
        });
        stats.blocksSkipped = incrementalCacheState ? incrementalCacheState.blocks.size : 0;

        return {
            document,
            attributes: { ...frontmatter.attributes, ...attributes },
            incrementalCacheState: getIncrementalCacheState({
                frontmatter,
                partialSource: partialMarkdownSource,
                partialDocument,
                nodeIndexes: context.nodeIndexes,
                previousIncrementalCacheState: incrementalCacheState,
                maxIncrementalOffset,
            }),
            stats,
            hasFrontmatter: frontmatter.bodyBegin > 0,
        };
    } catch (error) {
        logger.warning(
            `Failed to parse markdown document (inputLength=${input.length}): ${formatErrorForLog(error)}`
        );
        throw error;
    }
}

/**
 * Get the offset to the start of the given line.
 */
function getLineOffset(source: string, line: number): number {
    for (let offset = 0; offset < source.length; offset++) {
        if (source[offset] === '\n') {
            line--;
        }
        if (line === 0) {
            return offset;
        }
    }
    return -1;
}

function formatKB(bytes: number): string {
    return `${Math.round(bytes / 1024).toLocaleString('en-US')}KB`;
}
